import dayjs from "../../../configs/dayjs";
import { ColumnsType } from "antd/es/table";
import { DATE_FORMAT, COPIED_ACC_NUMBER } from "../../../lib/constants";
import { useMemo, useCallback } from "react";
import { useGetRole } from "../../../lib/hooks/getRole";
import { useStatusTag } from "./statusTag";
import { getFormattedCurrency } from "../../../lib/currencyFormatter";
import { Link, useSearchParams } from "react-router-dom";
import ROUTES from "../../../configs/routesConfig";
import { useGetColumnSortValue } from "../../../lib/hooks/useGetColumnSortValue";
import { CopyButton } from "../../../components/CopyButton";
import { usePermissions } from "../../../lib/hooks/usePermissions";
import { TableActions } from "../components/TableActions";
import { RightOutlined } from "@ant-design/icons";
import { Row } from "antd";

export const useAccountsTableColumns = (
  reloadData: () => void,
  navigateToAccount: (id: string) => void,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isAdmin] = useGetRole();

  const { getStatus } = useStatusTag();

  const { hasAccessByController } = usePermissions("Contracts");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [__, setSearchParams] = useSearchParams();

  const { getColumnSortValue } = useGetColumnSortValue();

  const noNextPaymentStatuses: string[] = useMemo(
    () => ["Default", "Paid Off", "Replaced", "Cancelled"],
    [],
  );

  const onSortChange = useCallback(
    (sorter: any, request: any) => {
      const newRequest = { ...request };
      if (!sorter.order) {
        delete newRequest["order"];
      } else {
        if (sorter.field === "accountNumber") {
          newRequest["order"] = sorter.order === "ascend" ? 0 : 1;
        }

        if (sorter.field === "status") {
          newRequest["order"] = sorter.order === "ascend" ? 2 : 3;
        }

        if (sorter.field === "nextPaymentDate") {
          newRequest["order"] = sorter.order === "ascend" ? 4 : 5;
        }
      }

      setSearchParams(newRequest);
    },
    [setSearchParams],
  );

  const columns = useMemo(() => {
    const value: ColumnsType<any> = [
      {
        title: `Account ${isAdmin ? "Number" : "#"}`,
        dataIndex: "accountNumber",
        key: "accountNumber",
        render: (value: string, record: any) => {
          const { id, accountNumber } = record;

          return (
            <>
              <Link
                to={`${ROUTES.ACCOUNTS}/${isAdmin ? id : accountNumber}`}
                className="elipsis"
                title={value}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="ant-btn-link">{value}</span>
              </Link>
              {isAdmin && (
                <CopyButton value={value} message={COPIED_ACC_NUMBER} />
              )}
              {record.xRef && (
                <div className="text-sm title">{record.xRef}</div>
              )}
            </>
          );
        },
        sorter: true,
        sortOrder: (() => getColumnSortValue("0", "1"))(),
        width: "13%",
      },
      {
        title: "Buyer",
        dataIndex: "buyer",
        key: "buyer",
        width: `${isAdmin ? "15" : "17"}%`,
        ellipsis: true,
        responsive: ["xl"],
      },
      {
        title: "Seller",
        dataIndex: "seller",
        key: "seller",
        width: `${isAdmin ? "15" : "17"}%`,
        ellipsis: true,
        responsive: ["xl"],
      },
      {
        title: "Property Address",
        dataIndex: "propertyAddress",
        key: "propertyAddress",
        width: `${isAdmin ? "14" : "18"}%`,
        ellipsis: true,
        render: (value: string) => (
          <div className="elipsis" title={value}>
            {value || "-"}
          </div>
        ),
        responsive: ["xl"],
      },
      {
        title: "Regular Amount",
        dataIndex: isAdmin ? "nextPaymentAmount" : "regularAmount",
        key: isAdmin ? "nextPaymentAmount" : "regularAmount",
        align: "right",
        render: (value: number, record: any) => {
          if (!isAdmin && noNextPaymentStatuses.includes(record.status)) {
            return <div />;
          }

          return value ? getFormattedCurrency(value || 0) : "-";
        },
        width: "13%",
        responsive: ["xl"],
      },
      {
        title: "Next Payment",
        dataIndex: "nextPaymentDate",
        key: "nextPaymentDate",
        ellipsis: true,
        render: (value: any, record: any) => {
          if (!isAdmin && noNextPaymentStatuses.includes(record.status)) {
            return <div />;
          }

          const dateToDisplay = value ? dayjs(value).format(DATE_FORMAT) : "-";

          const isPast = dayjs().isAfter(dayjs(value));

          return <span className={isPast ? "error" : ""}>{dateToDisplay}</span>;
        },
        sorter: isAdmin,
        sortOrder: (() => getColumnSortValue("4", "5"))(),
        width: "12%",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value: string | number) => getStatus(value),
        sorter: true,
        sortOrder: (() => getColumnSortValue("2", "3"))(),
        width: "11%",
      },
    ];

    const menuKeys: string[] = [];

    hasAccessByController("Modify") && menuKeys.push("1");

    hasAccessByController("Delete") && menuKeys.push("2");

    !isAdmin &&
      value.push({
        title: "",
        dataIndex: "",
        key: "",
        render: () => (
          <>
            <Row className="flex-nowrap">
              <span className="ant-btn-link view-btn">View</span>
              <RightOutlined className="ml-1 text-secondary text-md" />
            </Row>
          </>
        ),
        sorter: false,
        className: "view-btn-column",
      });

    isAdmin &&
      menuKeys.length > 0 &&
      value.push({
        title: "Action",
        render: (_: any, record: any) => {
          const { status, id } = record;

          return (
            <TableActions
              menuKeys={menuKeys}
              id={id}
              status={status}
              reloadData={reloadData}
              navigateToAccount={navigateToAccount}
            />
          );
        },
      });

    return value;
  }, [
    isAdmin,
    hasAccessByController,
    getColumnSortValue,
    noNextPaymentStatuses,
    getStatus,
    reloadData,
    navigateToAccount,
  ]);

  return { columns, onSortChange };
};
